/**
 * Core: Password Protection
 * -----------------------------------------------------------------------------
 * Handles password protection for products, collections, and pages.
 * - Checks for stored passwords on page load
 * - Shows/hides content based on password validation
 * - Creates password form when needed
 */

import { domReady } from '~/core/runners';

class PasswordProtection {
  constructor() {
    this.passwordData = null;
    this.template = null;
    this.handle = null;
    this.requiredPassword = null;
    this.form = null;
    this.overlay = null;
  }

  init() {
    // Check if password protection is needed
    if (!window.passwordProtection || !window.passwordProtection.data) {
      return;
    }

    this.passwordData = window.passwordProtection.data;
    this.template = window.passwordProtection.template;

    // Parse the password data (format: "handle:password")
    const parts = this.passwordData.split(':');
    if (parts.length !== 2) {
      return;
    }

    this.handle = parts[0];
    this.requiredPassword = parts[1];

    // Check if password is already stored
    this.checkStoredPassword();

    // If no valid password is stored, show the password form
    if (!this.isPasswordValid()) {
      this.showPasswordForm();
    }
  }

  checkStoredPassword() {
    try {
      const sessionKey = `password_${this.handle}`;
      const storedData = sessionStorage.getItem(sessionKey);

      if (storedData) {
        const data = JSON.parse(storedData);
        const storedPassword = data.password;
        const timestamp = data.timestamp;

        // Check if password is correct and not expired (24 hours)
        const now = Date.now();
        const expirationTime = 24 * 60 * 60 * 1000; // 24 hours

        if (
          storedPassword === this.requiredPassword &&
          now - timestamp < expirationTime
        ) {
          return true;
        }
        // Clear expired or incorrect password
        sessionStorage.removeItem(sessionKey);
      }
    } catch (error) {
      console.error('Error checking stored password:', error);
    }

    return false;
  }

  isPasswordValid() {
    return this.checkStoredPassword();
  }

  showPasswordForm() {
    // Hide the main content
    this.hideMainContent();

    // Create and show the password overlay
    this.createPasswordOverlay();
  }

  hideMainContent() {
    const mainContent = document.querySelector('#main-content');
    if (mainContent) {
      mainContent.style.display = 'none';
    }
  }

  showMainContent() {
    const mainContent = document.querySelector('#main-content');
    if (mainContent) {
      mainContent.style.display = 'block';
    }
  }

  createPasswordOverlay() {
    // Create overlay container
    this.overlay = document.createElement('div');
    this.overlay.className = 'password-protection-overlay';
    this.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.95);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: 2rem;
    `;

    // Create password form
    const formHTML = `
      <div style="
        max-width: 400px;
        width: 100%;
        text-align: center;
        background: white;
        padding: 2rem;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
      ">
        <h2 style="
          margin-bottom: 1rem;
          font-size: 1.5rem;
          font-weight: bold;
          color: #333;
        ">Password Protected</h2>

        <p style="
          margin-bottom: 2rem;
          color: #666;
          line-height: 1.5;
        ">This content requires a password to view. Please enter the password below to continue.</p>

        <form id="password-form" style="display: flex; flex-direction: column; gap: 1rem;">
          <input
            type="password"
            id="password-input"
            placeholder="Enter password"
            required
            style="
              padding: 0.75rem;
              border: 1px solid #ddd;
              border-radius: 4px;
              font-size: 1rem;
            "
          >

          <div id="password-error" style="
            color: #d32f2f;
            font-size: 0.875rem;
            display: none;
          ">Incorrect password. Please try again.</div>

          <button type="submit" style="
            padding: 0.75rem 1.5rem;
            background-color: #000;
            color: #fff;
            border: none;
            border-radius: 4px;
            font-size: 1rem;
            cursor: pointer;
            transition: background-color 0.2s;
          ">Unlock Content</button>
        </form>
      </div>
    `;

    this.overlay.innerHTML = formHTML;
    document.body.appendChild(this.overlay);

    // Bind form events
    this.form = document.getElementById('password-form');
    const input = document.getElementById('password-input');
    const error = document.getElementById('password-error');

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.handlePasswordSubmit(input, error);
    });

    input.addEventListener('input', () => {
      error.style.display = 'none';
    });

    // Focus the input
    input.focus();
  }

  handlePasswordSubmit(input, error) {
    const enteredPassword = input.value.trim();

    if (!enteredPassword) {
      error.style.display = 'block';
      return;
    }

    if (enteredPassword === this.requiredPassword) {
      this.storePassword(enteredPassword);
      this.showContent();
    } else {
      error.style.display = 'block';
      input.value = '';
      input.focus();
    }
  }

  storePassword(password) {
    try {
      const sessionKey = `password_${this.handle}`;
      const timestamp = Date.now();
      const data = {
        password,
        timestamp,
      };
      sessionStorage.setItem(sessionKey, JSON.stringify(data));
    } catch (error) {
      console.error('Error storing password:', error);
    }
  }

  showContent() {
    // Remove the password overlay
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }

    // Show the main content
    this.showMainContent();
  }
}

/**
 * Initialize password protection.
 */
domReady(() => {
  new PasswordProtection().init();
});
